import { supabase } from "@/integrations/supabase/client";

const PAGE_SIZE = 6;

function range(page: number, size = PAGE_SIZE) {
  const from = (page - 1) * size;
  return { from, to: from + size - 1 };
}

function paginate(count: number | null, page: number, size = PAGE_SIZE) {
  const total = count ?? 0;
  const pages = Math.max(1, Math.ceil(total / size));
  return { total, page, pages, hasMore: page < pages };
}

// Hides scheduled posts whose published_at is still in the future.
function notScheduled<T extends { or: (s: string) => T }>(q: T): T {
  const nowIso = new Date().toISOString();
  return q.or(`published_at.is.null,published_at.lte.${nowIso}`) as T;
}

export async function listArticles(opts: { category?: string; page?: number } = {}) {
  const page = opts.page ?? 1;
  const { from, to } = range(page);
  let q = (supabase as any)
    .from("articles_public")
    .select("*", { count: "exact" })
    .order("published_at", { ascending: false, nullsFirst: false })
    .range(from, to);
  if (opts.category) q = q.eq("category", opts.category);
  q = notScheduled(q);
  const { data, count, error } = await q;
  if (error) throw error;
  return { data: data ?? [], pagination: paginate(count, page) };
}

export async function getArticle(slug: string, opts: { preview?: boolean } = {}) {
  let data: any = null;
  if (opts.preview) {
    // Preview: fetch straight from base table (RLS allows admins/editors to read drafts).
    const { data: row, error } = await (supabase as any)
      .from("articles")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();
    if (error) throw error;
    data = row;
  } else {
    const nowIso = new Date().toISOString();
    const { data: row, error } = await (supabase as any)
      .from("articles_public")
      .select("*")
      .eq("slug", slug)
      .or(`published_at.is.null,published_at.lte.${nowIso}`)
      .maybeSingle();
    if (error) throw error;
    data = row;
  }
  if (!data) return null;
  const a: any = data;
  if (a.author_id) {
    const { data: author } = await (supabase as any)
      .from("authors")
      .select("name, role, bio, avatar_url")
      .eq("id", a.author_id)
      .maybeSingle();
    if (author) {
      a.author_name = a.author_name || author.name;
      a.author_role = a.author_role || author.role;
      a.author_avatar = a.author_avatar || author.avatar_url;
      a.author_bio = author.bio;
    }
  }
  const { data: related } = await notScheduled((supabase as any)
    .from("articles_public")
    .select("id,title,slug,excerpt,thumbnail,category,author_name,reading_time,published_at,featured")
    .neq("id", a.id)
    .order("featured", { ascending: false })
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(3));
  return { ...a, related: related ?? [], __preview: !!opts.preview };
}


export async function listInsights(opts: { type?: string; region?: string; page?: number } = {}) {
  const page = opts.page ?? 1;
  const { from, to } = range(page);
  let q = supabase.from("insights").select("*", { count: "exact" }).eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false }).range(from, to);
  if (opts.type) q = q.eq("insight_type", opts.type);
  if (opts.region) q = q.eq("primary_region", opts.region);
  q = notScheduled(q);
  const { data, count, error } = await q;
  if (error) throw error;
  return { data: data ?? [], pagination: paginate(count, page) };
}

export async function getInsight(slug: string, opts: { preview?: boolean } = {}) {
  let data: any = null;
  if (opts.preview) {
    const { data: row, error } = await supabase
      .from("insights")
      .select("*, author:authors(name, role, bio, avatar_url)")
      .eq("slug", slug)
      .maybeSingle();
    if (error) throw error;
    data = row;
  } else {
    const { data: row, error } = await notScheduled(supabase
      .from("insights")
      .select("*, author:authors(name, role, bio, avatar_url)")
      .eq("slug", slug).eq("published", true)).maybeSingle();
    if (error) throw error;
    data = row;
  }
  if (!data) return null;
  const r: any = data;
  if (r.author) {
    r.author_name = r.author_name || r.author.name;
    r.author_role = r.author_role || r.author.role;
    r.author_avatar = r.author_avatar || r.author.avatar_url;
    r.author_bio = r.author.bio;
  }
  const { data: related } = await notScheduled(supabase
    .from("insights")
    .select("id,title,slug,excerpt,thumbnail,insight_type,published_at,featured")
    .eq("published", true)
    .neq("id", r.id)
    .order("featured", { ascending: false })
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(3));
  return { ...r, related: related ?? [], __preview: !!opts.preview };
}

export async function listLearningPaths(opts: { difficulty?: string; page?: number; size?: number } = {}) {
  const page = opts.page ?? 1;
  const { from, to } = range(page, opts.size);
  let q = supabase.from("learning_paths").select("*, modules:learning_modules(*)", { count: "exact" })
    .eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false }).range(from, to);
  if (opts.difficulty) q = q.eq("difficulty", opts.difficulty);
  q = notScheduled(q);
  const { data, count, error } = await q;
  if (error) throw error;
  return { data: data ?? [], pagination: paginate(count, page) };
}

export async function getLearningPath(slug: string, opts: { preview?: boolean } = {}) {
  let data: any = null;
  if (opts.preview) {
    const { data: row, error } = await supabase
      .from("learning_paths")
      .select('*, modules:learning_modules(*)')
      .eq("slug", slug).maybeSingle();
    if (error) throw error;
    data = row;
  } else {
    const { data: row, error } = await notScheduled(supabase
      .from("learning_paths")
      .select('*, modules:learning_modules(*)')
      .eq("slug", slug).eq("published", true)).maybeSingle();
    if (error) throw error;
    data = row;
  }
  if (!data) return null;
  return { ...data, __preview: !!opts.preview };
}

// Columns safe to expose to anon (excludes landing_html and landing_content —
// premium fields revoked from anon at the DB level; served via get-material-content).
const MATERIAL_PUBLIC_COLS =
  "id,title,slug,description,thumbnail,file_url,file_type,file_size_mb,material_type,sector,primary_region,gated,gating_fields,meta_title,meta_description,views,download_clicks,gating_shows,gating_submitted,published_at,published,featured,created_at,updated_at,content,cta_layout,access_mode,blocks,status,approved_by,approved_at,download_links,material_includes,landing_template,landing_form_mode,landing_form_fields,landing_form_script";

export async function listMaterials(opts: { type?: string; sector?: string; page?: number } = {}) {
  const page = opts.page ?? 1;
  const { from, to } = range(page);
  let q = (supabase.from("materials_public" as any) as any).select(MATERIAL_PUBLIC_COLS, { count: "exact" }).eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false }).range(from, to);
  if (opts.type) q = q.eq("material_type", opts.type);
  if (opts.sector) q = q.eq("sector", opts.sector);
  q = notScheduled(q);
  const { data, count, error } = await q;
  if (error) throw error;
  return { data: data ?? [], pagination: paginate(count, page) };
}

export async function getMaterial(slug: string, opts: { preview?: boolean } = {}) {
  let data: any = null;
  if (opts.preview) {
    const { data: row, error } = await (supabase as any)
      .from("materials")
      .select("*")
      .eq("slug", slug).maybeSingle();
    if (error) throw error;
    data = row;
  } else {
    const { data: row, error } = await notScheduled((supabase.from("materials_public" as any) as any).select(MATERIAL_PUBLIC_COLS).eq("slug", slug).eq("published", true)).maybeSingle();
    if (error) throw error;
    data = row;
  }
  if (!data) return null;
  const m: any = data;
  const { data: related } = await notScheduled((supabase.from("materials_public" as any) as any)
    .select("id,title,slug,description,thumbnail,material_type,published_at,featured")
    .eq("published", true)
    .neq("id", m.id)
    .order("featured", { ascending: false })
    .order("published_at", { ascending: false, nullsFirst: false })
    .limit(3));
  return { ...m, related: (related ?? []).map((x: any) => ({ ...x, excerpt: x.description, category: x.material_type })), __preview: !!opts.preview };
}


export async function listCases(opts: { sector?: string; page?: number } = {}) {
  const page = opts.page ?? 1;
  const { from, to } = range(page);
  let q = supabase.from("case_studies").select("*", { count: "exact" }).eq("published", true)
    .order("published_at", { ascending: false, nullsFirst: false }).range(from, to);
  if (opts.sector) q = q.eq("client_sector", opts.sector);
  q = notScheduled(q);
  const { data, count, error } = await q;
  if (error) throw error;
  return { data: data ?? [], pagination: paginate(count, page) };
}

export async function getCase(slug: string, opts: { preview?: boolean } = {}) {
  let data: any = null;
  if (opts.preview) {
    const { data: row, error } = await supabase.from("case_studies").select("*").eq("slug", slug).maybeSingle();
    if (error) throw error;
    data = row;
  } else {
    const { data: row, error } = await notScheduled(supabase.from("case_studies").select("*").eq("slug", slug).eq("published", true)).maybeSingle();
    if (error) throw error;
    data = row;
  }
  if (!data) return null;
  return { ...data, __preview: !!opts.preview };
}

export async function listEvents(opts: { page?: number } = {}) {
  const page = opts.page ?? 1;
  const { from, to } = range(page);
  const { data, count, error } = await (supabase as any)
    .from("events")
    .select("*", { count: "exact" })
    .eq("published", true)
    .order("date", { ascending: true, nullsFirst: false })
    .range(from, to);
  if (error) throw error;
  return { data: data ?? [], pagination: paginate(count, page) };
}

export async function listGlossaryTerms() {
  const { data, error } = await (supabase as any)
    .from("glossary_terms")
    .select("*")
    .eq("published", true)
    .order("term", { ascending: true });
  if (error) throw error;
  return data ?? [];
}
